import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SlideShell from '../SlideShell';

export default function SelectDropdownSlide({
  title,
  subtitle,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  options = [],
  fieldName,
  initialValue = '',
  label = '',
  placeholder = 'Select an option',
  nextLabel = 'Continue',
  animationDirection = 'forward',
  isTransitioning = false,
  theme = 'light',
  onAutoSave = null,
}) {
  const normalizedOptions = useMemo(
    () =>
      options.map((option) =>
        typeof option === 'string'
          ? { value: option, label: option }
          : {
              value: option.value ?? '',
              label: option.label ?? option.value ?? '',
            },
      ),
    [options],
  );

  const [selectedValue, setSelectedValue] = useState(initialValue ?? '');
  const lastCommittedRef = useRef(JSON.stringify(initialValue ?? ''));

  useEffect(() => {
    const nextValue = initialValue ?? '';
    setSelectedValue(nextValue);
    lastCommittedRef.current = JSON.stringify(nextValue);
  }, [initialValue]);

  const commitAutoSave = useCallback(
    (value) => {
      if (!onAutoSave || !fieldName) return;
      const payload = { [fieldName]: value || null };
      const serialized = JSON.stringify(payload);
      if (lastCommittedRef.current === serialized) return;
      lastCommittedRef.current = serialized;
      onAutoSave(payload);
    },
    [fieldName, onAutoSave],
  );

  const handleChange = (event) => {
    const value = event.target.value;
    setSelectedValue(value);
    commitAutoSave(value);
  };

  const handleContinue = () => {
    if (!fieldName || !selectedValue) return;
    commitAutoSave(selectedValue);
    onNext?.({ [fieldName]: selectedValue });
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && selectedValue) {
      event.preventDefault();
      handleContinue();
    }
  };

  const disabled = !selectedValue;
  const isDark = theme === 'dark';

  return (
    <SlideShell
      title={title}
      subtitle={subtitle}
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleContinue}
      onBack={onBack}
      nextLabel={nextLabel}
      disabled={disabled}
      animationDirection={animationDirection}
      isTransitioning={isTransitioning}
      theme={theme}
    >
      <div className="w-full max-w-md lg:max-w-none mx-auto">
        {label && (
          <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            {label}
          </p>
        )}
        <div className="relative">
          <select
            value={selectedValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            className={[
              'w-full appearance-none rounded-xl border px-4 py-3 text-base lg:text-lg lg:px-5 lg:py-3.5',
              'focus:outline-none focus:ring-2 focus:ring-black focus:border-black',
              isDark ? 'bg-mm_black border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900',
            ].join(' ')}
          >
            <option value="">{placeholder}</option>
            {normalizedOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              aria-hidden="true"
              className={isDark ? 'text-gray-400' : 'text-gray-500'}
            >
              <path
                d="M4 6l4 4 4-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </div>
    </SlideShell>
  );
}
